import { createContext, ReactNode, useContext, useEffect } from 'react';
import { JWTContextType } from '../types';
import { useAuthFunctions, useAuth } from '../hook';
import { readStoredToken } from '../constants';

export const AuthContext = createContext<JWTContextType | null>(null);
const AuthProvider = ({ children, baseUrl }: { children: ReactNode; baseUrl: string }) => {
  const {
    state,
    login,
    logout,
    signUp,
    resetPassword,
    initializeAuthentication,
    verifyToken,
    invalidateSession,
    initialize,
  } = useAuthFunctions(baseUrl);

  useEffect(() => {
    // Unguarded `localStorage.getItem` here would re-create the crash this provider sits above:
    // a WebView with site data disabled exposes `localStorage` as **null**, and React routes an
    // error thrown in an effect to the nearest ErrorBoundary, so the page would still break —
    // just one line later than it used to.
    const accessToken = readStoredToken() || undefined;
    initialize(accessToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        logout,
        signUp,
        resetPassword,
        initializeAuthentication,
        verifyToken,
        invalidateSession,
        initialize,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuthContext = () => useContext(AuthContext);
export { AuthProvider, useAuth, useAuthContext };
